import { useState } from 'react';
import type { ReactElement } from 'react';
import { Link } from 'react-router-dom';
import { TOKEN_ANZEIGESTATUS_BESCHRIFTUNG } from '@mcikm-vote/shared';
import type { Durchfuehrungsstand, TokenUebersicht } from '@mcikm-vote/shared';

import { useAbstimmungAktion } from '../../admin/abstimmungenApi';
import { useDurchfuehrung } from '../../admin/durchfuehrungApi';
import { useTokens, useVeranstaltungAktion } from '../../admin/veranstaltungenApi';
import { BestaetigungsDialog } from '../../components/BestaetigungsDialog';
import { Fehlermeldung } from '../../components/Fehlermeldung';
import { Kennzahlen } from '../../components/Kennzahlen';
import { Knopf } from '../../components/Knopf';
import { StatusZeichen } from '../../components/StatusZeichen';
import { formatiereZeitpunkt } from '../../formatierung';

/**
 * Durchführungsmodus (§20.3).
 *
 * Die zentrale Ansicht während einer Veranstaltung: **eine** Seite, auf der alles steht, was
 * während der Durchführung zählt, und von der aus jede Aktion erreichbar ist. Die Vorbereitung
 * bleibt in den Abstimmungsseiten; hier geht es nur um den laufenden Betrieb.
 *
 * Drei Festlegungen:
 *
 * - **Keine Verteilung.** Der Durchführungsstand hat dafür kein Feld (§16); angezeigt werden
 *   ausschließlich die Zahlen aus §15.
 * - **Jede kritische Aktion braucht eine Bestätigung** und sendet den Status mit, den diese Seite
 *   angezeigt hat (§25.3). Hat sich der Serverstand geändert, wird die Aktion abgewiesen und die
 *   Anzeige erneuert.
 * - **Warnungen sind Hinweise, keine Sperren.** §6.4 lässt das Öffnen unabhängig von der
 *   Teilnehmerzahl ausdrücklich zu.
 */

type Aktion =
  | 'oeffnen'
  | 'schliessen'
  | 'annullieren'
  | 'veroeffentlichen'
  | 'anzeige-beenden'
  | 'warteraum'
  | 'veranstaltung-beenden';

interface Dialogtext {
  titel: string;
  bestaetigung: string;
  gefahr: boolean;
}

const DIALOG: Record<Aktion, Dialogtext> = {
  oeffnen: { titel: 'Abstimmung öffnen', bestaetigung: 'Jetzt öffnen', gefahr: false },
  schliessen: { titel: 'Abstimmung schließen', bestaetigung: 'Endgültig schließen', gefahr: true },
  annullieren: { titel: 'Abstimmung annullieren', bestaetigung: 'Annullieren', gefahr: true },
  veroeffentlichen: {
    titel: 'Ergebnis veröffentlichen',
    bestaetigung: 'Veröffentlichen',
    gefahr: false,
  },
  'anzeige-beenden': {
    titel: 'Ergebnisanzeige beenden',
    bestaetigung: 'Anzeige beenden',
    gefahr: false,
  },
  warteraum: {
    titel: 'Alle Teilnehmer in den Warteraum',
    bestaetigung: 'In den Warteraum',
    gefahr: false,
  },
  'veranstaltung-beenden': {
    titel: 'Veranstaltung endgültig beenden',
    bestaetigung: 'Endgültig beenden',
    gefahr: true,
  },
};

export function DurchfuehrungSeite(): ReactElement {
  const stand = useDurchfuehrung();
  const [gewaehlt, setGewaehlt] = useState<Aktion | undefined>(undefined);

  const veranstaltungId = stand.data?.veranstaltung?.id ?? '';
  // Die Abstimmungskennung wechselt mit dem Fortschritt der Veranstaltung; der Hook hängt an ihr.
  const abstimmungId = stand.data?.geoeffnet?.id ?? stand.data?.naechste?.id ?? '';
  const ergebnisId = stand.data?.zuletztBeendet?.id ?? '';
  // Eigener Bezug: Eingeblendet ist nicht zwingend die zuletzt beendete Abstimmung (ÄA-4).
  const eingeblendetId = stand.data?.eingeblendet?.id ?? '';

  const abstimmungAktion = useAbstimmungAktion(abstimmungId);
  const ergebnisAktion = useAbstimmungAktion(ergebnisId);
  const anzeigeAktion = useAbstimmungAktion(eingeblendetId);
  const veranstaltungAktion = useVeranstaltungAktion(veranstaltungId);
  const tokens = useTokens(veranstaltungId);

  if (stand.isPending) {
    return <p className="text-text-gedaempft text-sm">Wird geladen …</p>;
  }

  if (stand.isError) {
    return <Fehlermeldung>{stand.error.message}</Fehlermeldung>;
  }

  const daten: Durchfuehrungsstand = stand.data;

  if (daten.veranstaltung === null) {
    return (
      <div className="flex flex-col gap-4">
        <h1 className="text-xl font-semibold">Durchführung</h1>
        <p className="border-rand bg-flaeche text-text-gedaempft rounded-xl border p-4 text-sm">
          Derzeit ist keine Veranstaltung freigegeben oder laufend. Systemweit darf immer nur eine
          Veranstaltung aktiv sein.{' '}
          <Link to="/verwaltung/veranstaltungen" className="text-akzent underline">
            Zu den Veranstaltungen
          </Link>
        </p>
      </div>
    );
  }

  const fehler =
    abstimmungAktion.error ??
    ergebnisAktion.error ??
    anzeigeAktion.error ??
    veranstaltungAktion.error;
  const laeuft =
    abstimmungAktion.isPending ||
    ergebnisAktion.isPending ||
    anzeigeAktion.isPending ||
    veranstaltungAktion.isPending;

  function fuehreAus(): void {
    if (gewaehlt === undefined || daten.veranstaltung === null) {
      return;
    }

    const fertig = { onSuccess: () => setGewaehlt(undefined) };

    /**
     * Setzt `erwarteterStatus` nur, wenn er bekannt ist (§25.3).
     *
     * Ein `undefined` **auszulassen** ist etwas anderes, als es zu senden: Der Server behandelt ein
     * fehlendes Feld als „keine Vorbedingung". Genau das ist hier gemeint, wenn die Ansicht den
     * Zustand gar nicht kennt.
     */
    const mitStatus = <T extends string>(
      aktion: T,
      status: string | undefined,
    ): { aktion: T; erwarteterStatus?: string } => ({
      aktion,
      ...(status === undefined ? {} : { erwarteterStatus: status }),
    });

    switch (gewaehlt) {
      case 'oeffnen':
        abstimmungAktion.mutate(mitStatus('oeffnen', daten.naechste?.status), fertig);
        return;
      case 'schliessen':
        abstimmungAktion.mutate(mitStatus('schliessen', daten.geoeffnet?.status), fertig);
        return;
      case 'annullieren':
        abstimmungAktion.mutate(mitStatus('annullieren', daten.geoeffnet?.status), fertig);
        return;
      case 'veroeffentlichen':
        ergebnisAktion.mutate(
          mitStatus('ergebnis/veroeffentlichen', daten.zuletztBeendet?.status),
          fertig,
        );
        return;
      case 'anzeige-beenden':
        anzeigeAktion.mutate(mitStatus('ergebnis/beenden', daten.eingeblendet?.status), fertig);
        return;
      case 'warteraum':
        veranstaltungAktion.mutate(mitStatus('warteraum', daten.veranstaltung.status), fertig);
        return;
      case 'veranstaltung-beenden':
        veranstaltungAktion.mutate(mitStatus('beenden', daten.veranstaltung.status), fertig);
        return;
    }
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold">Durchführung</h1>
          <p className="text-text-gedaempft text-sm">
            {daten.veranstaltung.bezeichnung} · Code {daten.veranstaltung.code}
          </p>
        </div>
        <StatusZeichen
          beschriftung={daten.veranstaltung.status === 'laufend' ? 'Laufend' : 'Freigegeben'}
          tonart={daten.veranstaltung.status === 'laufend' ? 'erfolg' : 'neutral'}
        />
      </div>

      {fehler !== null && fehler !== undefined && <Fehlermeldung>{fehler.message}</Fehlermeldung>}

      {daten.warnungen.length > 0 && (
        <ul className="border-warnung/40 bg-flaeche-gedaempft flex flex-col gap-1 rounded-md border p-3 text-sm">
          {daten.warnungen.map((warnung) => (
            <li key={warnung.art}>{warnung.text}</li>
          ))}
        </ul>
      )}

      <section className="border-rand bg-flaeche rounded-xl border p-4 shadow-sm">
        <h2 className="mb-3 text-lg font-medium">Teilnehmer</h2>
        <Kennzahlen
          gross
          zahlen={[
            { beschriftung: 'Angemeldet', wert: daten.teilnehmer.angemeldet },
            { beschriftung: 'Verbunden', wert: daten.teilnehmer.verbunden },
            { beschriftung: 'Sitzung getrennt', wert: daten.teilnehmer.getrennt },
          ]}
        />
        <p className="text-text-gedaempft mt-3 text-xs">
          Ein getrenntes Gerät bleibt anwesend: Die Anwesenheit endet erst durch Abmeldung,
          administrative Abmeldung, Tokensperre oder Veranstaltungsende.
        </p>
      </section>

      <section className="border-rand bg-flaeche rounded-xl border p-4 shadow-sm">
        <h2 className="mb-3 text-lg font-medium">
          {daten.geoeffnet === null ? 'Nächste Abstimmung' : 'Geöffnete Abstimmung'}
        </h2>

        {daten.geoeffnet !== null ? (
          <>
            <p className="font-medium">
              Abstimmung {daten.geoeffnet.nummer ?? '—'}: {daten.geoeffnet.ueberschrift}
            </p>
            <p className="text-text-gedaempft mt-1 text-xs">
              Geöffnet seit{' '}
              {daten.geoeffnet.geoeffnetAm === null
                ? '—'
                : formatiereZeitpunkt(daten.geoeffnet.geoeffnetAm)}
            </p>

            {/* §15: ausschließlich diese Zahlen — keine Verteilung, kein Prozentwert (§16). */}
            <div className="mt-4">
              <Kennzahlen
                gross
                zahlen={[
                  {
                    beschriftung: 'Berücksichtigte Teilnehmer',
                    wert: daten.beteiligung?.beruecksichtigteTeilnehmer,
                  },
                  {
                    beschriftung: 'Abgegebene Stimmen',
                    wert: daten.beteiligung?.abgegebeneStimmen,
                  },
                  {
                    beschriftung: 'Noch nicht abgegeben',
                    wert: daten.beteiligung?.nichtAbgegebeneStimmen,
                  },
                ]}
              />
            </div>

            <div className="mt-4 flex flex-wrap gap-2">
              <Knopf
                onClick={() => {
                  setGewaehlt('schliessen');
                }}
              >
                Abstimmung schließen
              </Knopf>
              <Knopf
                art="gefahr"
                onClick={() => {
                  setGewaehlt('annullieren');
                }}
              >
                Annullieren
              </Knopf>
              <Link
                to={`/verwaltung/abstimmungen/${daten.geoeffnet.id}`}
                className="border-rand bg-flaeche-gedaempft min-h-touchflaeche hover:bg-rand flex items-center rounded-md border px-3 text-sm"
              >
                Zur Abstimmung
              </Link>
            </div>
          </>
        ) : daten.naechste !== null ? (
          <>
            <p className="font-medium">{daten.naechste.ueberschrift}</p>
            <p className="text-text-gedaempft mt-1 text-xs">
              Als bereit markiert. Vor dem Öffnen erscheint eine Bestätigung mit Vorschau in der
              Abstimmungsansicht.
            </p>
            <div className="mt-4 flex flex-wrap gap-2">
              <Knopf
                onClick={() => {
                  setGewaehlt('oeffnen');
                }}
              >
                Nächste Abstimmung öffnen
              </Knopf>
              <Link
                to={`/verwaltung/abstimmungen/${daten.naechste.id}`}
                className="border-rand bg-flaeche-gedaempft min-h-touchflaeche hover:bg-rand flex items-center rounded-md border px-3 text-sm"
              >
                Vorschau ansehen
              </Link>
            </div>
          </>
        ) : (
          <p className="text-text-gedaempft text-sm">
            Es ist keine Abstimmung als bereit markiert.{' '}
            <Link
              to={`/verwaltung/veranstaltungen/${daten.veranstaltung.id}/abstimmungen`}
              className="text-akzent underline"
            >
              Zu den Abstimmungen
            </Link>
          </p>
        )}
      </section>

      {daten.zuletztBeendet !== null && (
        <section className="border-rand bg-flaeche rounded-xl border p-4 shadow-sm">
          <h2 className="mb-3 text-lg font-medium">Zuletzt beendet</h2>
          <p className="font-medium">
            Abstimmung {daten.zuletztBeendet.nummer ?? '—'}: {daten.zuletztBeendet.ueberschrift}
          </p>
          <p className="mt-1">
            <StatusZeichen
              beschriftung={
                daten.zuletztBeendet.status === 'annulliert'
                  ? 'Annulliert — keine Veröffentlichung'
                  : daten.zuletztBeendet.ergebnisVeroeffentlicht
                    ? 'Ergebnis veröffentlicht'
                    : 'Ergebnis noch nicht veröffentlicht'
              }
              tonart={
                daten.zuletztBeendet.status === 'annulliert'
                  ? 'neutral'
                  : daten.zuletztBeendet.ergebnisVeroeffentlicht
                    ? 'erfolg'
                    : 'warnung'
              }
            />
          </p>

          {daten.zuletztBeendet.status === 'geschlossen' && (
            <div className="mt-4 flex flex-wrap gap-2">
              {/*
                Nur das Veröffentlichen steht hier. „Ergebnisanzeige beenden" gehört ausschließlich
                an die Abstimmung, die tatsächlich eingeblendet ist (Meldung 20260801-2130-02) —
                das ist nicht zwingend diese, siehe den Abschnitt darunter.
              */}
              {!daten.zuletztBeendet.ergebnisVeroeffentlicht && (
                <Knopf
                  onClick={() => {
                    setGewaehlt('veroeffentlichen');
                  }}
                >
                  Ergebnis veröffentlichen
                </Knopf>
              )}
              <Link
                to={`/verwaltung/abstimmungen/${daten.zuletztBeendet.id}`}
                className="border-rand bg-flaeche-gedaempft min-h-touchflaeche hover:bg-rand flex items-center rounded-md border px-3 text-sm"
              >
                Ergebnis ansehen
              </Link>
            </div>
          )}
        </section>
      )}

      {daten.eingeblendet !== null && (
        <section className="border-rand bg-flaeche rounded-xl border p-4 shadow-sm">
          <h2 className="mb-3 text-lg font-medium">Bei den Teilnehmern eingeblendet</h2>
          <p className="font-medium">
            Abstimmung {daten.eingeblendet.nummer ?? '—'}: {daten.eingeblendet.ueberschrift}
          </p>
          <p className="mt-1">
            <StatusZeichen beschriftung="Ergebnis wird angezeigt" tonart="erfolg" />
          </p>
          <div className="mt-4 flex flex-wrap gap-2">
            <Knopf
              art="sekundaer"
              onClick={() => {
                setGewaehlt('anzeige-beenden');
              }}
            >
              Ergebnisanzeige beenden
            </Knopf>
            <Link
              to={`/verwaltung/abstimmungen/${daten.eingeblendet.id}`}
              className="border-rand bg-flaeche-gedaempft min-h-touchflaeche hover:bg-rand flex items-center rounded-md border px-3 text-sm"
            >
              Ergebnis ansehen
            </Link>
          </div>
        </section>
      )}

      <section className="border-rand bg-flaeche rounded-xl border p-4 shadow-sm">
        <h2 className="mb-3 text-lg font-medium">Anwesende Teilnehmer</h2>
        <TeilnehmerListe zeilen={tokens.data ?? []} />
        <div className="mt-4 flex flex-wrap gap-2">
          <Link
            to={`/verwaltung/veranstaltungen/${daten.veranstaltung.id}/tokens`}
            className="border-rand bg-flaeche-gedaempft min-h-touchflaeche hover:bg-rand flex items-center rounded-md border px-3 text-sm"
          >
            Teilnehmer- und Tokenverwaltung
          </Link>
        </div>
      </section>

      <section className="border-rand bg-flaeche rounded-xl border p-4 shadow-sm">
        <h2 className="mb-3 text-lg font-medium">Veranstaltung</h2>
        <div className="flex flex-wrap gap-2">
          <Knopf
            art="sekundaer"
            disabled={daten.geoeffnet !== null}
            onClick={() => {
              setGewaehlt('warteraum');
            }}
          >
            Alle Teilnehmer in den Warteraum
          </Knopf>
          <Knopf
            art="gefahr"
            onClick={() => {
              setGewaehlt('veranstaltung-beenden');
            }}
          >
            Veranstaltung beenden
          </Knopf>
        </div>
        {daten.geoeffnet !== null && (
          <p className="text-text-gedaempft mt-2 text-xs">
            Solange eine Abstimmung geöffnet ist, hat sie bei den Teilnehmern Vorrang; die
            Rücksetzung in den Warteraum wäre wirkungslos.
          </p>
        )}
      </section>

      {gewaehlt !== undefined && (
        <BestaetigungsDialog
          titel={DIALOG[gewaehlt].titel}
          beschreibung={<Beschreibung aktion={gewaehlt} stand={daten} />}
          bestaetigungstext={DIALOG[gewaehlt].bestaetigung}
          gefahr={DIALOG[gewaehlt].gefahr}
          laeuft={laeuft}
          onBestaetigen={fuehreAus}
          onAbbrechen={() => {
            setGewaehlt(undefined);
          }}
        />
      )}
    </div>
  );
}

/** Die anwesenden Teilnehmer (§9.8). Nie die Auswahl — nur, **ob** abgestimmt wurde. */
function TeilnehmerListe({ zeilen }: { zeilen: TokenUebersicht[] }): ReactElement {
  const anwesend = zeilen.filter((zeile) =>
    ['angemeldet', 'sitzung_getrennt'].includes(zeile.anzeigestatus),
  );

  if (anwesend.length === 0) {
    return (
      <p className="text-text-gedaempft text-sm">Es ist derzeit kein Teilnehmer angemeldet.</p>
    );
  }

  return (
    <ul className="flex flex-col gap-1 text-sm">
      {anwesend.map((zeile) => (
        <li key={zeile.id} className="flex flex-wrap items-center justify-between gap-2">
          <span className="font-mono">{zeile.token}</span>
          <span className="flex flex-wrap items-center gap-2">
            <StatusZeichen
              beschriftung={TOKEN_ANZEIGESTATUS_BESCHRIFTUNG[zeile.anzeigestatus]}
              tonart={zeile.anzeigestatus === 'angemeldet' ? 'erfolg' : 'warnung'}
            />
            {zeile.hatAbgestimmt === null ? null : (
              <StatusZeichen
                beschriftung={zeile.hatAbgestimmt ? 'Stimme abgegeben' : 'Noch nicht abgestimmt'}
                tonart={zeile.hatAbgestimmt ? 'erfolg' : 'neutral'}
              />
            )}
          </span>
        </li>
      ))}
    </ul>
  );
}

/**
 * Der Text des Bestätigungsdialogs.
 *
 * Er nennt die Folgen und — beim Schließen — die Zahlen aus §17.2. Ein Dialog, der nur „Wirklich?"
 * fragt, hilft bei einer unwiderruflichen Aktion nicht weiter.
 */
function Beschreibung({
  aktion,
  stand,
}: {
  aktion: Aktion;
  stand: Durchfuehrungsstand;
}): ReactElement {
  switch (aktion) {
    case 'oeffnen':
      return (
        <>
          Die Abstimmung wird für alle angemeldeten Teilnehmer sichtbar; Inhalt und Regeln sind
          danach unveränderlich.
          {stand.teilnehmer.angemeldet === 0 && (
            <>
              {' '}
              <strong>
                Derzeit ist kein Teilnehmer angemeldet. Die Abstimmung kann dennoch geöffnet werden;
                eine Beschlussfähigkeit prüft die Anwendung nicht.
              </strong>
            </>
          )}
        </>
      );
    case 'schliessen':
      return (
        <>
          Die Abstimmung wird endgültig geschlossen und kann nicht erneut geöffnet werden. Eine
          weitere Stimmabgabe ist danach ausgeschlossen. Derzeit:{' '}
          {stand.beteiligung?.beruecksichtigteTeilnehmer ?? 0} berücksichtigte Teilnehmer,{' '}
          {stand.beteiligung?.abgegebeneStimmen ?? 0} abgegebene Stimmen,{' '}
          {stand.beteiligung?.nichtAbgegebeneStimmen ?? 0} noch nicht abgegeben.
        </>
      );
    case 'annullieren':
      return (
        <>
          Die Abstimmung wird endgültig verworfen. Alle bereits abgegebenen Stimmzettel werden
          gelöscht, ein Ergebnis wird nicht veröffentlicht, und eine Wiederholung ist nur als neue
          Abstimmung möglich. Ein interner Grund lässt sich in der Abstimmungsansicht angeben.
        </>
      );
    case 'veroeffentlichen':
      return (
        <>
          Das Ergebnis erscheint auf allen verbundenen Teilnehmergeräten. Die Veröffentlichung kann
          jederzeit wieder beendet werden.
        </>
      );
    case 'anzeige-beenden':
      return (
        <>
          Die Anzeige der Abstimmung {stand.eingeblendet?.nummer ?? '—'} endet; die Teilnehmer sehen
          wieder den neutralen Abschlusshinweis. Zahlen, Exporte und Protokoll bleiben unverändert.
        </>
      );
    case 'warteraum':
      return (
        <>
          Auf den Teilnehmergeräten erscheint wieder der Warteraum; die zuletzt beendete Abstimmung
          samt Ergebnis wird ihnen nicht mehr angezeigt, und eine laufende Ergebnisanzeige endet.
          Ergebnisse, Exporte und Protokolle bleiben unverändert.
        </>
      );
    case 'veranstaltung-beenden':
      return (
        <>
          Die Veranstaltung wird endgültig beendet. Alle Teilnehmersitzungen werden ungültig, eine
          Anmeldung ist nicht mehr möglich, und die Veranstaltung kann nicht wieder aktiviert
          werden. Ergebnisse, Exporte und Protokolle bleiben erhalten.
        </>
      );
  }
}
